<template>
  <el-drawer
    title="Route for today"
    v-model="isShow"
    direction="btt"
    class="createTask"
    size="100%"
    @open="openRoute()"
  >
    <div class="route">
      <el-row :gutter="10">
        <el-col :span="12">
          <div class="route__label">Current location</div>
          <el-input
            v-model="address"
            placeholder="Enter address"
            :disabled="locating"
          >
            <template #append>
              <el-button @click="detectPosition()" :loading="locating">
                <el-icon><Location /></el-icon>
              </el-button>
            </template>
          </el-input>
        </el-col>
        <el-col :span="12">
          <div class="route__label">Zones</div>
          <el-select
            v-model="zoneIds"
            placeholder="Select zones"
            class="w-100"
            multiple
            collapse-tags
            :clearable="true"
          >
            <el-option
              v-for="item in zoneList"
              :key="item.id"
              :label="item.title"
              :value="item.id"
            />
          </el-select>
        </el-col>
        <el-col :span="24" class="route__colors">
          <div class="route__label">Task colors</div>
          <el-select
            v-model="colors"
            placeholder="All colors"
            class="w-100"
            multiple
            collapse-tags
            :clearable="true"
          >
            <el-option
              v-for="item in colorList"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            >
              <span class="route__circle" :style="{ background: item.color }" />
              <span>{{ item.label }}</span>
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-divider />

      <div class="route__building" v-if="building">
        <el-icon class="is-loading"><Loading /></el-icon>
        <p>Building route</p>
        <span>It may take a few minutes, you can close this window</span>
      </div>

      <div class="route__list" v-else-if="points.length">
        <div
          class="route__item"
          v-for="(item, key) in points"
          :key="key"
          @click="openPoint(item)"
        >
          <div class="route__marks">
            <el-icon v-for="mark in item.completed" :key="mark">
              <SuccessFilled />
            </el-icon>
          </div>
          <div class="route__order">{{ item.order }}</div>
          <div class="route__info">
            <p class="route__name">{{ item.name }}</p>
            <el-tag size="mini">{{ item.address }}</el-tag>
            <el-tag size="mini" type="warning">
              {{ `Tasks: ${item.completed} / ${item.total}` }}
            </el-tag>
            <el-tag size="mini" type="info" v-if="item.workingTime">
              {{ item.workingTime }}
            </el-tag>
          </div>
        </div>
      </div>
      <el-empty description="Route is not built" v-else></el-empty>

      <div class="route__submit">
        <el-button
          type="primary"
          class="w-100"
          :loading="loading || building"
          @click="generate()"
        >
          Calculate route
        </el-button>
      </div>
    </div>
  </el-drawer>

  <RoutePoint v-model="isPointModal" :point="activePoint || {}" />
</template>

<script>
import ApiRoute from "@/api/route";
import RoutePoint from "@/components/Task/RoutePoint";
import bus from "vue3-eventbus";
import _ from "lodash";

export default {
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    RoutePoint,
  },
  data() {
    return {
      zoneList: [],
      zoneIds: [],
      colorList: [
        { value: "red", label: "Red", color: "#ea092f" },
        { value: "orange", label: "Orange", color: "#E6A23C" },
        { value: "green", label: "Green", color: "#67C23A" },
      ],
      colors: ["red"],
      points: [],
      address: "",
      latitude: null,
      longitude: null,
      loading: false,
      locating: false,
      building: false,
      poller: null,
      isPointModal: false,
      activePoint: null,
    };
  },
  mounted() {
    bus.on("compliteTask", this.refreshCounters);
  },
  beforeUnmount() {
    this.stopPolling();
    bus.off("compliteTask", this.refreshCounters);
  },
  computed: {
    isShow: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  methods: {
    openRoute() {
      this.getZoneList();
      this.getRoute();
    },
    openPoint(point) {
      this.activePoint = point;
      this.isPointModal = true;
    },
    refreshCounters() {
      if (this.isShow && !this.building) {
        this.getRoute(true);
      }
    },
    getZoneList() {
      ApiRoute.getZoneList().then((responce) => {
        this.zoneList = responce.status ? responce.data : [];
      });
    },
    getRoute(silent = false) {
      ApiRoute.getRoute().then((responce) => {
        const route = responce.status ? responce.data.route : null;

        if (!route) {
          this.points = [];
          this.building = false;
          this.stopPolling();
          if (!silent) {
            this.detectPosition();
          }
          return;
        }

        this.zoneIds = route.zoneIds;
        this.colors = route.colors || [];
        this.address = route.address;
        this.latitude = route.latitude;
        this.longitude = route.longitude;

        if (route.status === "pending") {
          this.building = true;
          this.startPolling();
          return;
        }

        this.building = false;
        this.stopPolling();

        if (route.status === "failed") {
          this.points = [];
          this.$message.error("Failed to build the route, please try again");
          return;
        }

        this.points = route.points;
      });
    },
    startPolling() {
      if (this.poller) {
        return;
      }

      this.poller = setInterval(() => this.getRoute(true), 3000);
    },
    stopPolling() {
      if (this.poller) {
        clearInterval(this.poller);
        this.poller = null;
      }
    },
    detectPosition() {
      if (!navigator.geolocation || !window.isSecureContext) {
        this.$message.warning(
          "Geolocation is not available, enter the address manually"
        );
        return;
      }

      this.locating = true;
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.latitude = position.coords.latitude;
          this.longitude = position.coords.longitude;
          this.getAddress();
        },
        () => {
          this.locating = false;
          this.latitude = null;
          this.longitude = null;
          this.$message.warning(
            "Failed to detect location, enter the address manually"
          );
        },
        { enableHighAccuracy: true, timeout: 15000 }
      );
    },
    getAddress() {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${this.latitude}&lon=${this.longitude}`;

      fetch(url)
        .then((responce) => responce.json())
        .then((data) => {
          this.address = data.display_name || this.getCoordinates();
          this.locating = false;
        })
        .catch(() => {
          this.address = this.getCoordinates();
          this.locating = false;
        });
    },
    getCoordinates() {
      return `${this.latitude}, ${this.longitude}`;
    },
    generate() {
      if (!this.address) {
        this.$message.error("Enter your current location");
        return;
      }

      if (!this.zoneIds.length) {
        this.$message.error("Select at least one zone");
        return;
      }

      this.loading = true;
      ApiRoute.generate({
        address: this.address,
        latitude: this.latitude,
        longitude: this.longitude,
        zoneIds: this.zoneIds,
        colors: this.colors,
      }).then((responce) => {
        this.loading = false;

        if (!responce.status || !responce.data.route) {
          this.$message.error(
            _.first(responce.data) || "Failed to calculate the route"
          );
          return;
        }

        this.points = [];
        this.building = true;
        this.startPolling();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.route {
  &__label {
    font-size: 12px;
    color: #909399;
    padding-bottom: 5px;
  }

  &__colors {
    padding-top: 10px;
  }

  &__circle {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 100%;
    margin-right: 8px;
  }

  &__list {
    padding-bottom: 60px;
  }

  &__building {
    text-align: center;
    padding: 40px 15px;
    color: #909399;

    .el-icon {
      font-size: 28px;
      color: #409eff;
    }

    p {
      margin-top: 10px;
      font-size: 15px;
      color: #303133;
    }

    span {
      font-size: 12px;
    }
  }

  &__item {
    position: relative;
    display: flex;
    align-items: flex-start;
    padding: 10px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin-bottom: 10px;
    cursor: pointer;
    transition: border-color 0.25s;

    &:hover {
      border-color: #409eff;
    }
  }

  &__marks {
    position: absolute;
    top: 5px;
    right: 8px;
    color: #67c23a;
    font-size: 16px;
  }

  &__order {
    width: 26px;
    height: 26px;
    min-width: 26px;
    margin-right: 10px;
    border-radius: 100%;
    background: #409eff;
    color: #fff;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__name {
    font-weight: 500;
    margin-bottom: 5px;
    padding-right: 60px;
  }

  &__comment {
    font-size: 12px;
    color: #909399;
    padding-top: 5px;
  }

  &__submit {
    position: fixed;
    left: 15px;
    right: 15px;
    bottom: 15px;
  }
}
</style>
